import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import {
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../../../common/global/helpers';
import Button from '../../../../../../../common/widgets/Button';
import Dialog from '../../../../../../../common/widgets/Dialog';
import Password from '../../../../../../../common/widgets/Password';
import { ErrorState, ErrorType } from '../../../../../../benefit/models/Errors';
import { resources } from '../../../../../resources/resources';
import { merchantAccountsService } from '../../../../../services/merchant-accounts';

interface ChangePasswordDialogProps {
  closeDialog: () => void;
}
interface ChangePasswordForm {
  oldPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

export const ChangePasswordDialog = (props: ChangePasswordDialogProps) => {
  const { closeDialog } = props;
  const { t: translate } = useTranslation();
  const [form, setForm] = useState<ChangePasswordForm>({});
  const [errors, setErrors] = useState<ErrorState>({});
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);
  const auth = useAuth();

  const clearErrors = (field: string) => {
    setErrors(
      produce(d => {
        d[field] = [];
      }),
    );
  };

  const onSaveClick = async () => {
    setIsSaveLoading(true);
    const resp = await merchantAccountsService.updatePassword({
      ...form,
    });
    setIsSaveLoading(false);
    if (resp.hasErrors) {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      showSuccessToast(
        translate(
          resources.accounts.myAccount.changePassword.dialog.confirmation,
        ),
      );
      closeDialog();
      setTimeout(async () => {
        await auth.signoutRedirect();
      }, 2000);
    }
  };

  return (
    <Dialog
      closeDialog={closeDialog}
      showCloseIcon
      className="dialog-merchants-edit"
    >
      <div className="dialog-content">
        <div className="dialog-title">
          {translate(resources.accounts.myAccount.changePassword.dialog.title)}
        </div>
        <div className="dialog-text centered mb-15">
          {translate(resources.accounts.myAccount.changePassword.dialog.info)}
        </div>
        <div className="m-dialog-password-rules mb-30">
          <ul className="password-rules">
            <li>
              {translate(
                resources.accounts.myAccount.changePassword.dialog.rule1,
              )}
            </li>
            <li>
              {translate(
                resources.accounts.myAccount.changePassword.dialog.rule2,
              )}
            </li>
            <li>
              {translate(
                resources.accounts.myAccount.changePassword.dialog.rule3,
              )}
            </li>
          </ul>
        </div>

        <Password
          label={translate(
            resources.accounts.myAccount.changePassword.dialog.currentPassword,
          )}
          value={form.oldPassword}
          onChange={e =>
            setForm(
              produce(d => {
                d.oldPassword = e.value;
              }),
            )
          }
          errors={errors.oldPassword}
          clearErrors={() => clearErrors('oldPassword')}
          mandatory
        />

        <Password
          label={translate(
            resources.accounts.myAccount.changePassword.dialog.newPassword,
          )}
          value={form.newPassword}
          onChange={e =>
            setForm(
              produce(d => {
                d.newPassword = e.value;
              }),
            )
          }
          errors={errors.newPassword}
          clearErrors={() => clearErrors('newPassword')}
          mandatory
        />

        <Password
          label={translate(
            resources.accounts.myAccount.changePassword.dialog
              .confirmNewPassword,
          )}
          value={form.confirmPassword}
          onChange={e =>
            setForm(
              produce(d => {
                d.confirmPassword = e.value;
              }),
            )
          }
          errors={errors.confirmPassword}
          clearErrors={() => clearErrors('confirmPassword')}
          mandatory
          controlClassName="mb-0"
        />
      </div>

      <div className="dialog-actions vertical">
        <Button
          className="btn btn-solid"
          onClick={onSaveClick}
          loading={isSaveLoading}
        >
          <span className="btn-text">
            {translate(
              resources.accounts.myAccount.changePassword.dialog.button,
            )}
          </span>
        </Button>
      </div>
    </Dialog>
  );
};
